import { BasePrinterAdapter } from './BasePrinterAdapter'
import type {
  PrinterConnectionConfig,
  PrinterTelemetry,
  PrinterCapabilities,
  CameraStream,
  MoonrakerState,
} from '../printerConnection.types'

export class MoonrakerAdapter extends BasePrinterAdapter {
  private ws: WebSocket | null = null
  private jsonRpcId = 0

  constructor(config: PrinterConnectionConfig) {
    super({ ...config, port: config.port || 7125 })
  }

  async connect(): Promise<void> {
    this.setConnectionStatus('connecting')

    try {
      // Test connection with server info
      const response = await fetch(this.buildUrl('/server/info'))
      if (!response.ok) {
        throw new Error(`Connection failed: ${response.statusText}`)
      }

      const info = await response.json()
      this.state.firmwareInfo = {
        name: 'Klipper',
        version: info.result?.klippy_state || 'unknown',
      }

      // Get printer info
      const printerInfo = await fetch(this.buildUrl('/printer/info')).then((r) => r.json())
      if (printerInfo.result) {
        this.state.firmwareInfo.version = printerInfo.result.software_version || 'unknown'
      }

      // Get capabilities
      this.state.capabilities = await this.getCapabilities()

      // Connect WebSocket for real-time updates
      this.connectWebSocket()

      this.setConnectionStatus('connected')
      this.state.printerStatus = 'idle'
      this.startPolling()
    } catch (error) {
      this.state.lastError = error instanceof Error ? error.message : 'Connection failed'
      this.setConnectionStatus('error')
      throw error
    }
  }

  async disconnect(): Promise<void> {
    this.stopPolling()
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
    this.setConnectionStatus('disconnected')
    this.state.printerStatus = 'offline'
  }

  private connectWebSocket(): void {
    const wsUrl = this.buildWsUrl('/websocket')
    this.ws = new WebSocket(wsUrl)

    this.ws.onopen = () => {
      // Subscribe to printer objects for real-time updates
      this.sendJsonRpc('printer.objects.subscribe', {
        objects: {
          extruder: ['temperature', 'target'],
          heater_bed: ['temperature', 'target'],
          print_stats: ['state', 'filename', 'total_duration', 'print_duration', 'filament_used'],
          display_status: ['progress', 'message'],
          virtual_sdcard: ['progress', 'file_position'],
          toolhead: ['position'],
          fan: ['speed'],
        },
      })
    }

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        this.handleWebSocketMessage(data)
      } catch {
        // Ignore parse errors
      }
    }

    this.ws.onerror = () => {
      console.error('Moonraker WebSocket error')
    }

    this.ws.onclose = () => {
      if (this.state.connectionStatus === 'connected' && this.config.autoReconnect) {
        this.handleDisconnect()
      }
    }
  }

  private sendJsonRpc(method: string, params?: object): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          jsonrpc: '2.0',
          method,
          params,
          id: ++this.jsonRpcId,
        })
      )
    }
  }

  private handleWebSocketMessage(data: { method?: string; params?: [number, MoonrakerState] }): void {
    if (data.method === 'notify_status_update' && data.params) {
      const status = data.params[0] as unknown as MoonrakerState

      // Update printer status
      if (status.print_stats?.state) {
        switch (status.print_stats.state) {
          case 'printing':
            this.state.printerStatus = 'printing'
            break
          case 'paused':
            this.state.printerStatus = 'paused'
            break
          case 'complete':
          case 'cancelled':
          case 'standby':
            this.state.printerStatus = 'idle'
            break
          case 'error':
            this.state.printerStatus = 'error'
            break
        }
        this.state.currentFile = status.print_stats.filename
      }

      // Build telemetry from status update
      const telemetry: PrinterTelemetry = {
        timestamp: new Date(),
        temperatures: {
          nozzle: {
            actual: (status as any).extruder?.temperature ?? this.state.telemetry?.temperatures.nozzle.actual ?? 0,
            target: (status as any).extruder?.target ?? this.state.telemetry?.temperatures.nozzle.target ?? 0,
          },
          bed: {
            actual: status.heater_bed?.temperature ?? this.state.telemetry?.temperatures.bed.actual ?? 0,
            target: status.heater_bed?.target ?? this.state.telemetry?.temperatures.bed.target ?? 0,
          },
        },
      }

      if (status.print_stats) {
        telemetry.printTimeElapsed = status.print_stats.print_duration
        telemetry.filamentUsed = status.print_stats.filament_used
      }

      if (status.display_status?.progress !== undefined) {
        telemetry.printProgress = status.display_status.progress * 100
      } else if (status.virtual_sdcard?.progress !== undefined) {
        telemetry.printProgress = status.virtual_sdcard.progress * 100
      }

      if (status.toolhead?.position && Array.isArray(status.toolhead.position)) {
        const pos = status.toolhead.position
        telemetry.position = {
          x: pos[0] ?? 0,
          y: pos[1] ?? 0,
          z: pos[2] ?? 0,
          e: pos[3],
        }
      }

      this.updateTelemetry(telemetry)
    }
  }

  async getTelemetry(): Promise<PrinterTelemetry> {
    const response = await fetch(
      this.buildUrl('/printer/objects/query?extruder&heater_bed&print_stats&virtual_sdcard&toolhead&fan')
    )
    const data = await response.json()
    const status = data.result?.status || {}

    const telemetry: PrinterTelemetry = {
      timestamp: new Date(),
      temperatures: {
        nozzle: {
          actual: status.extruder?.temperature ?? 0,
          target: status.extruder?.target ?? 0,
        },
        bed: {
          actual: status.heater_bed?.temperature ?? 0,
          target: status.heater_bed?.target ?? 0,
        },
      },
    }

    if (status.print_stats) {
      telemetry.printTimeElapsed = status.print_stats.print_duration
      telemetry.filamentUsed = status.print_stats.filament_used
      this.state.currentFile = status.print_stats.filename

      switch (status.print_stats.state) {
        case 'printing':
          this.state.printerStatus = 'printing'
          break
        case 'paused':
          this.state.printerStatus = 'paused'
          break
        case 'complete':
        case 'cancelled':
        case 'standby':
          this.state.printerStatus = 'idle'
          break
        case 'error':
          this.state.printerStatus = 'error'
          break
      }
    }

    if (status.virtual_sdcard?.progress !== undefined) {
      telemetry.printProgress = status.virtual_sdcard.progress * 100
    }

    if (status.toolhead?.position && Array.isArray(status.toolhead.position)) {
      const pos = status.toolhead.position
      telemetry.position = {
        x: pos[0] ?? 0,
        y: pos[1] ?? 0,
        z: pos[2] ?? 0,
        e: pos[3],
      }
    }

    if (status.fan?.speed !== undefined) {
      telemetry.fanSpeed = status.fan.speed * 100
    }

    return telemetry
  }

  async getCapabilities(): Promise<PrinterCapabilities> {
    try {
      const response = await fetch(this.buildUrl('/printer/objects/query?configfile'))
      const data = await response.json()
      const config = data.result?.status?.configfile?.config || {}

      // Extract build volume from stepper config
      const stepperX = config.stepper_x || {}
      const stepperY = config.stepper_y || {}
      const stepperZ = config.stepper_z || {}

      return {
        hasCamera: true, // Usually has camera via crowsnest/mjpg-streamer
        hasEnclosure: !!config.heater_generic?.chamber || !!config.temperature_sensor?.chamber,
        hasMultiMaterial: !!config.mmu || Object.keys(config).some((k) => k.startsWith('extruder1')),
        hasAutoLeveling: !!config.bed_mesh || !!config.probe || !!config.bltouch,
        hasFilamentSensor: !!config.filament_switch_sensor || !!config.filament_motion_sensor,
        hasPowerMonitor: !!config.power,
        maxNozzleTemp: parseFloat(config.extruder?.max_temp) || 300,
        maxBedTemp: parseFloat(config.heater_bed?.max_temp) || 120,
        buildVolume: {
          x: Math.abs(parseFloat(stepperX.position_max) - parseFloat(stepperX.position_min || 0)) || 250,
          y: Math.abs(parseFloat(stepperY.position_max) - parseFloat(stepperY.position_min || 0)) || 250,
          z: Math.abs(parseFloat(stepperZ.position_max) - parseFloat(stepperZ.position_min || 0)) || 250,
        },
      }
    } catch {
      return {
        hasCamera: true,
        hasEnclosure: false,
        hasMultiMaterial: false,
        hasAutoLeveling: true,
        hasFilamentSensor: false,
        hasPowerMonitor: false,
        maxNozzleTemp: 300,
        maxBedTemp: 120,
        buildVolume: { x: 250, y: 250, z: 250 },
      }
    }
  }

  async getCameraStream(): Promise<CameraStream | null> {
    // Try common Klipper camera endpoints
    const port = this.config.port === 7125 ? 8080 : this.config.port
    return {
      url: `http://${this.config.host}:${port}/webcam/?action=stream`,
      type: 'mjpeg',
    }
  }

  async startPrint(filename: string): Promise<void> {
    await fetch(this.buildUrl('/printer/print/start'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename }),
    })
    this.state.printerStatus = 'printing'
    this.emitEvent('print_started', { filename })
  }

  async pausePrint(): Promise<void> {
    await fetch(this.buildUrl('/printer/print/pause'), { method: 'POST' })
    this.state.printerStatus = 'paused'
    this.emitEvent('print_paused')
  }

  async resumePrint(): Promise<void> {
    await fetch(this.buildUrl('/printer/print/resume'), { method: 'POST' })
    this.state.printerStatus = 'printing'
    this.emitEvent('print_resumed')
  }

  async cancelPrint(): Promise<void> {
    await fetch(this.buildUrl('/printer/print/cancel'), { method: 'POST' })
    this.state.printerStatus = 'idle'
    this.emitEvent('print_cancelled')
  }

  async setNozzleTemp(temp: number): Promise<void> {
    await this.sendGcode(`SET_HEATER_TEMPERATURE HEATER=extruder TARGET=${temp}`)
  }

  async setBedTemp(temp: number): Promise<void> {
    await this.sendGcode(`SET_HEATER_TEMPERATURE HEATER=heater_bed TARGET=${temp}`)
  }

  async setChamberTemp(temp: number): Promise<void> {
    await this.sendGcode(`SET_HEATER_TEMPERATURE HEATER=chamber TARGET=${temp}`)
  }

  async homeAxes(axes?: ('x' | 'y' | 'z')[]): Promise<void> {
    const axisStr = axes?.map((a) => a.toUpperCase()).join(' ') || ''
    await this.sendGcode(`G28 ${axisStr}`.trim())
  }

  async jogAxis(axis: 'x' | 'y' | 'z', distance: number): Promise<void> {
    await this.sendGcode('G91') // Relative positioning
    await this.sendGcode(`G1 ${axis.toUpperCase()}${distance} F3000`)
    await this.sendGcode('G90') // Absolute positioning
  }

  async listFiles(): Promise<string[]> {
    const response = await fetch(this.buildUrl('/server/files/list?root=gcodes'))
    const data = await response.json()
    return (data.result || []).map((f: { path: string }) => f.path)
  }

  async uploadFile(file: File, filename?: string): Promise<void> {
    const formData = new FormData()
    formData.append('file', file, filename || file.name)
    formData.append('root', 'gcodes')

    await fetch(this.buildUrl('/server/files/upload'), {
      method: 'POST',
      body: formData,
    })
  }

  async deleteFile(filename: string): Promise<void> {
    await fetch(this.buildUrl(`/server/files/gcodes/${filename}`), {
      method: 'DELETE',
    })
  }

  private async sendGcode(script: string): Promise<void> {
    await fetch(this.buildUrl('/printer/gcode/script'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ script }),
    })
  }
}
